/**
 * useGuestFetch
 * One read for a guest route: loading, the data, or why it was refused. A
 * refusal (link expired/replaced/legacy/invalid, nothing delegated) is kept
 * typed so the route can render the matching parked page; `notFound` means
 * the task is not (or no longer) delegated to the user; any other failure is
 * a plain message.
 */
import { useEffect, useRef, useState } from 'react';
import { guestLinkRefusalFromError, GuestLinkRefusalError } from '../../../api/guestLinkRefusal';

export interface GuestFetchState<T> {
    data: T | null;
    loading: boolean;
    refusal: GuestLinkRefusalError | null;
    error: string | null;
    notFound: boolean;
}

/**
 * @param load  The read to run. Re-run when `key` changes; identity is ignored.
 * @param key   Identifies the request (token + task key); null skips the read.
 */
export function useGuestFetch<T>(load: () => Promise<T>, key: string | null): GuestFetchState<T> {
    const loadRef = useRef(load);
    loadRef.current = load;
    const [state, setState] = useState<GuestFetchState<T>>({ data: null, loading: key !== null, refusal: null, error: null, notFound: false });

    useEffect(() => {
        if (key === null) {
            setState({ data: null, loading: false, refusal: null, error: 'Länken är inte giltig', notFound: false });
            return;
        }
        let mounted = true;
        setState({ data: null, loading: true, refusal: null, error: null, notFound: false });
        loadRef.current()
            .then((data) => {
                if (mounted) setState({ data, loading: false, refusal: null, error: null, notFound: false });
            })
            .catch((err: unknown) => {
                if (!mounted) return;
                const refusal = guestLinkRefusalFromError(err);
                const notFound = (err as { code?: unknown } | null)?.code === 'functions/not-found';
                if (!refusal && !notFound) console.error('Guest route error:', err);
                setState({
                    data: null,
                    loading: false,
                    refusal,
                    error: refusal ? null : (err instanceof Error && err.message ? err.message : 'Kunde inte ladda'),
                    notFound,
                });
            });
        return () => {
            mounted = false;
        };
    }, [key]);

    return state;
}
